import React, { useCallback } from "react";
import { Box, Flex, Grid, Heading, Stack, Text } from "@chakra-ui/react";
import maplibregl from "maplibre-gl";
import { useTranslation } from "react-i18next";
import { captureException } from "@sentry/nextjs";

import Badge from "scenes/Watchdog/components/Badge";
import { EApiWatchdogFrequencyId, IWatchdog } from "utils/watchdog/types";
import { convertBoundsToMapbox } from "helpers/convertBoundsToMap";
import CustomLink, { ILinkProps } from "components/Chakra/CustomLink";
import Button from "components/Chakra/Button";
import IconEdit from "components/Chakra/Icons/Edit";
import IconNotification from "components/Chakra/Icons/Notification";
import IconTrash from "components/Chakra/Icons/Trash";
import useViewport from "hooks/useViewport";

interface IWatchdogProps {
  wd: IWatchdog;
  linkProps?: ILinkProps;
  activateDog?: () => void;
  setEdit?: () => void;
  deleteDog: () => void;
  inSearch?: boolean;
}

const Watchdog: React.FC<IWatchdogProps> = ({
  wd,
  linkProps,
  activateDog,
  setEdit,
  deleteDog,
  inSearch,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const refInstance = React.useRef<maplibregl.Map | null>(null);
  const { current: mapInstace } = refInstance;

  const WD_OPTIONS = React.useMemo(
    () => ({
      [EApiWatchdogFrequencyId.NEVER]: t("watchdogFrequency.never"),
      [EApiWatchdogFrequencyId.IMMEDIATE]: t("watchdogFrequency.immediate"),
      [EApiWatchdogFrequencyId.ONCE_PER_8_HOURS]: t("watchdogFrequency.asap"),
      [EApiWatchdogFrequencyId.ONCE_PER_DAY]: t("watchdogFrequency.oncePerDay"),
      [EApiWatchdogFrequencyId.ONCE_PER_WEEK]: t(
        "watchdogFrequency.oncePerWeek",
      ),
    }),
    [t],
  );

  const initMap = useCallback((): maplibregl.Map | null => {
    try {
      const mapInst = new maplibregl.Map({
        boxZoom: true,
        interactive: true,
        container: `wd-${wd.id}`, // container id
        style: "https://tiles.ulovdomov.cz/styles/basic-preview/style.json",
        bounds: convertBoundsToMapbox(wd.filter.bounds),
      });

      document.getElementsByClassName("mapboxgl-ctrl-bottom-right")[0].remove();
      document.getElementsByClassName("mapboxgl-ctrl-bottom-left")[0].remove();

      mapInst.on("load", () => {
        // @ts-ignore
        mapInst.fitBounds(convertBoundsToMapbox(wd.filter.bounds), {
          padding: 40,
          animate: false,
        });

        mapInst.addSource(`wd-layer-${wd.id}`, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                id: `wd-layout-${wd.id}`,
                type: "Feature",
                properties: {},
                geometry: {
                  type: "Polygon",
                  coordinates: [
                    [
                      [
                        wd.filter.bounds.northEast.lng,
                        wd.filter.bounds.southWest.lat,
                      ], // NW
                      [
                        wd.filter.bounds.southWest.lng,
                        wd.filter.bounds.southWest.lat,
                      ], // SW
                      [
                        wd.filter.bounds.southWest.lng,
                        wd.filter.bounds.northEast.lat,
                      ], // SE
                      [
                        wd.filter.bounds.northEast.lng,
                        wd.filter.bounds.northEast.lat,
                      ], // NE
                      [
                        wd.filter.bounds.northEast.lng,
                        wd.filter.bounds.southWest.lat,
                      ], // NW
                    ],
                  ],
                },
              },
            ],
          },
        });

        mapInst.addLayer({
          id: "park-boundary",
          type: "fill",
          source: `wd-layer-${wd.id}`,
          paint: {
            "fill-color": "#e74c3c",
            "fill-opacity": 0.3,
          },
          filter: ["==", "$type", "Polygon"],
        });

        mapInst.addLayer({
          id: "states-layer-outline",
          type: "line",
          source: `wd-layer-${wd.id}`,
          paint: {
            "line-color": "#e74c3c",
            "line-opacity": 0.5,
            "line-width": 2,
          },
          filter: ["==", "$type", "Polygon"],
        });
      });

      return mapInst;
    } catch (err) {
      captureException(err);

      return null;
    }
  }, [wd.filter.bounds, wd.id]);

  React.useEffect(() => {
    if (mapInstace) {
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, mapInstace]);

  React.useEffect(() => {
    const mapInst = initMap();

    refInstance.current = mapInst;
  }, [initMap]);

  const actions = (
    <>
      <Stack direction="row" alignItems="center" flexWrap="nowrap">
        <CustomLink
          showUnderline
          {...linkProps}
          trackEventPayload={{
            "dl.eCat": "watchdog",
            "dl.eAct": "upravit_psa",
          }}
          onClick={() => {
            setEdit && setEdit();
            window.scrollTo(0, 100);
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="nowrap"
            spacing={0.5}
            data-test="editDogButton"
          >
            <IconEdit fillWith="ud-error" />
            <Box display={["none", "block"]}>
              <Text variant="small_2" textColor="primary">
                Upravit psa
              </Text>
            </Box>
          </Stack>
        </CustomLink>
      </Stack>
      <Stack
        data-test="deleteButton"
        direction="row"
        alignItems="center"
        flexWrap="nowrap"
      >
        <Button
          variant="link"
          trackEventPayload={{
            "dl.eCat": "watchdog",
            "dl.eAct": "smazat_psa",
          }}
          onClick={() => {
            deleteDog();
            window.scrollTo(0, 100);
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="nowrap"
            spacing={0.5}
          >
            <IconTrash fillWith="ud-error" />
            <Box display={["none", "block"]}>
              <Text variant="small_2" textColor="primary">
                Smazat psa
              </Text>
            </Box>
          </Stack>
        </Button>
      </Stack>
    </>
  );

  return (
    <Box
      data-test="myDogSingleValue"
      key={wd.id}
      opacity={wd.frequencyId !== EApiWatchdogFrequencyId.NEVER ? "unset" : 0.7}
    >
      <Grid
        templateColumns={["1fr", "250px auto"]}
        transition="box-shadow 200ms"
        boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.19)"
        _hover={{
          boxShadow: "0 0 16px 0 rgba(0, 0, 0, 0.19)",
        }}
        bg="white"
      >
        <Box
          width={["100%", "250px"]}
          height={["200px", "auto"]}
          id={`wd-${wd.id}`}
        />
        <Stack w="100%" p="15px" spacing="20px" justify="space-between">
          <Heading as="h3" textAlign="left" data-test="myDogCardHeading">
            {wd.name.length > 30 ? `${wd.name.slice(0, 30)}...` : wd.name}
          </Heading>
          {wd.frequencyId === EApiWatchdogFrequencyId.NEVER ? (
            <Stack direction="row" data-test="dogStatus">
              <Badge>Neaktivní</Badge>
              <Badge>Vypnuto</Badge>
            </Stack>
          ) : (
            <Stack direction="row" data-test="dogStatusActive">
              <Badge type="success">Aktivní</Badge>
              <Badge>
                <>
                  <IconNotification fillWith="ud-grey-600" />
                  {WD_OPTIONS[wd.frequencyId]}
                </>
              </Badge>
            </Stack>
          )}
          {wd.frequencyId === EApiWatchdogFrequencyId.NEVER ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              data-test="activeMyDogButton"
            >
              <CustomLink onClick={() => activateDog && activateDog()}>
                <Text size="small" textColor="primary">
                  Aktivovat psa
                </Text>
              </CustomLink>
            </Stack>
          ) : (
            <Stack w="100%" spacing="20px">
              <Stack display={["none", "flex"]} direction="row" spacing="20px">
                {actions}
              </Stack>
              <Stack
                data-test="showMeAllOffersForThisDog"
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                spacing="20px"
                justify="space-between"
              >
                {!inSearch && (
                  <CustomLink
                    trackEventPayload={{
                      "dl.eCat": "watchdog",
                      "dl.eAct": "zobrazit_inzeráty",
                    }}
                    to="/nastaveni/hlidaci-pes/[id]"
                    routeProps={{ id: wd.id.toString() }}
                    variant="button"
                    size="sm"
                    w={["inherit", "100%"]}
                  >
                    Zobrazit inzeráty
                  </CustomLink>
                )}
                <Flex display={["flex", "none"]}>{actions}</Flex>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Grid>
    </Box>
  );
};

export default Watchdog;
